import { handleUnauthorized } from '@utils/apiHelpers'
import { buildWidgetListUrl } from '@utils/widgetListApi'
import { t } from '@store/I18nStore'

export type TrafficSummary = {
  totalChats: number
  newUsers: number
  returningUsers: number
  missedConversations: number
  missedRate: number
  concurrentChatsMax: number
  avgIncomingPerHour: number
  peakThreshold: number
}

export type TrafficChannelBreakdown = {
  channelType: string
  count: number
  percent: number
  newUsers: number
  returningUsers: number
  missed: number
}

export type TrafficPeakHour = {
  hourStart: string
  incomingCount: number
  concurrentChatsMax: number
  missedCount: number
  isPeak: boolean
}

export type TrafficReportData = {
  summary: TrafficSummary
  channelsBreakdown: TrafficChannelBreakdown[]
  peakLoadGrid: TrafficPeakHour[]
}

export type OperatorsReportPeriodType = 'day' | 'week' | 'month' | 'custom'

export type OperatorReportItem = {
  operatorId: number
  email: string
  displayName: string
  title: string | null
  avatarUrl: string | null
  role: 'MANAGER' | 'OPERATOR' | string
  acceptedDialogs: number
  missedDialogs: number
  transferredDialogs: number
  onlineSeconds: number
  breakSeconds: number
  avgAcceptanceSeconds: number
  avgFirstResponseSeconds: number
  avgHandlingSeconds: number
  /** @deprecated Prefer totalRatings / averageRating (1–5 scale). */
  positiveRatings?: number
  /** @deprecated Prefer totalRatings / averageRating (1–5 scale). */
  negativeRatings?: number
  totalRatings: number
  averageRating: number | null
  ratingPercent: number | null
  /** Counts per star 1–5, e.g. `{ "1": 0, "5": 3 }`. */
  ratingCounts?: Record<string, number>
}

export type OperatorsReportRatingItem = {
  rating: number
  count: number
  percent: number
}

/** График оценок: пока available=false — UI блок не показываем. */
export type OperatorsReportChart = {
  type: 'ratings' | string
  available: boolean
  items: OperatorsReportRatingItem[]
  totals: {
    /** Legacy thumbs totals (optional). */
    positive?: number
    negative?: number
    ratingCounts?: Record<string, number>
    totalRatings?: number
    averageRating?: number | null
    ratingPercent: number | null
  }
}

export type OperatorsReportPagination = {
  page: number
  perPage: number
  total: number
  lastPage: number
  hasMore: boolean
}

export type OperatorsReportPeriod = {
  type: OperatorsReportPeriodType
  dateFrom: string
  dateTo: string
}

export type OperatorsReportData = {
  items: OperatorReportItem[]
  chart: OperatorsReportChart
  pagination: OperatorsReportPagination
  period: OperatorsReportPeriod
}

export type OperatorsReportSortBy =
  | 'acceptedDialogs'
  | 'missedDialogs'
  | 'transferredDialogs'
  | 'onlineSeconds'
  | 'breakSeconds'
  | 'avgAcceptanceSeconds'
  | 'avgFirstResponseSeconds'
  | 'avgHandlingSeconds'
  | 'averageRating'
  | 'totalRatings'
  | 'ratingPercent'
  | string

export type OperatorsReportQueryParams = {
  widgetId?: number
  period: OperatorsReportPeriodType
  page?: number
  perPage?: number
  sortBy?: OperatorsReportSortBy
  order?: 'asc' | 'desc'
  utcOffsetMinutes?: number
  dateFrom?: string
  dateTo?: string
}

/** Начальная выборка сотрудников: 4 строки + кнопка «Показать всех» при hasMore. */
export const OPERATORS_REPORT_INITIAL_PER_PAGE = 4

export type ReportChannelOption = {
  id: number
  widgetId: number
  channelType: string
  name: string
}

export type ReportQueryParams = {
  widgetId?: number
  dateFrom: string
  dateTo: string
}

export function buildReportQuery(params: ReportQueryParams): string {
  const q = new URLSearchParams()
  if (params.widgetId != null && Number.isFinite(params.widgetId)) {
    q.set('widgetId', String(params.widgetId))
  }
  q.set('dateFrom', params.dateFrom)
  q.set('dateTo', params.dateTo)
  return q.toString()
}

type TrafficReportResult =
  | { ok: true; data: TrafficReportData }
  | { ok: false; status: number; notFound?: boolean }

const inflightTrafficReports = new Map<string, Promise<TrafficReportResult>>()

async function fetchTrafficReportOnce(
  base: string,
  params: ReportQueryParams,
  authHeaders: Record<string, string>,
): Promise<TrafficReportResult> {
  const url = `${base}/api/reports/traffic?${buildReportQuery(params)}`
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
  })
  if (handleUnauthorized(res)) {
    return { ok: false, status: 401 }
  }
  if (res.status === 404) {
    return { ok: false, status: 404, notFound: true }
  }
  if (!res.ok) {
    return { ok: false, status: res.status }
  }
  const json = await res.json()
  if (!json?.success || !json?.data) {
    return { ok: false, status: res.status }
  }
  return { ok: true, data: json.data as TrafficReportData }
}

export function fetchTrafficReport(
  base: string,
  params: ReportQueryParams,
  authHeaders: Record<string, string>,
): Promise<TrafficReportResult> {
  const normalizedBase = base.replace(/\/$/, '')
  const dedupeKey = `${normalizedBase}|${buildReportQuery(params)}|${authHeaders.Authorization ?? ''}`
  const existing = inflightTrafficReports.get(dedupeKey)
  if (existing) return existing

  const promise = fetchTrafficReportOnce(normalizedBase, params, authHeaders).finally(() => {
    if (inflightTrafficReports.get(dedupeKey) === promise) {
      inflightTrafficReports.delete(dedupeKey)
    }
  })
  inflightTrafficReports.set(dedupeKey, promise)
  return promise
}

export function buildOperatorsReportQuery(params: OperatorsReportQueryParams): string {
  const q = new URLSearchParams()
  if (params.widgetId != null && Number.isFinite(params.widgetId)) {
    q.set('widgetId', String(params.widgetId))
  }
  q.set('period', params.period)
  q.set('page', String(params.page ?? 1))
  q.set('perPage', String(params.perPage ?? OPERATORS_REPORT_INITIAL_PER_PAGE))
  q.set('sortBy', params.sortBy ?? 'acceptedDialogs')
  q.set('order', params.order ?? 'desc')
  const utcOffset =
    params.utcOffsetMinutes ?? -new Date().getTimezoneOffset()
  q.set('utcOffsetMinutes', String(utcOffset))
  if (params.period === 'custom') {
    if (params.dateFrom) q.set('dateFrom', params.dateFrom)
    if (params.dateTo) q.set('dateTo', params.dateTo)
  }
  return q.toString()
}

/** UI-пресет фильтра дат → period для GET /api/reports/operators */
export function reportDatePresetToPeriod(
  preset: 'today' | 'last7' | 'last30',
): Exclude<OperatorsReportPeriodType, 'custom'> {
  if (preset === 'today') return 'day'
  if (preset === 'last30') return 'month'
  return 'week'
}

export async function fetchOperatorsReport(
  base: string,
  params: OperatorsReportQueryParams,
  authHeaders: Record<string, string>,
): Promise<
  { ok: true; data: OperatorsReportData } | { ok: false; status: number; notFound?: boolean }
> {
  const url = `${base}/api/reports/operators?${buildOperatorsReportQuery(params)}`
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
  })
  if (handleUnauthorized(res)) {
    return { ok: false, status: 401 }
  }
  if (res.status === 404) {
    return { ok: false, status: 404, notFound: true }
  }
  if (!res.ok) {
    return { ok: false, status: res.status }
  }
  const json = await res.json()
  if (!json?.success || !json?.data) {
    return { ok: false, status: res.status }
  }
  return { ok: true, data: normalizeOperatorsReportData(json.data as OperatorsReportData) }
}

function normalizeRatingCounts(
  raw: Record<string, number> | undefined | null,
): Record<string, number> | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const out: Record<string, number> = {}
  let any = false
  for (const key of ['1', '2', '3', '4', '5'] as const) {
    const n = Number((raw as Record<string, unknown>)[key] ?? 0)
    if (Number.isFinite(n) && n > 0) any = true
    out[key] = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0
  }
  return any || Object.keys(raw).length > 0 ? out : undefined
}

function normalizeOperatorsReportData(data: OperatorsReportData): OperatorsReportData {
  const items = (data.items ?? []).map((item) => {
    const totalRatings = Number(item.totalRatings ?? 0) || 0
    const averageRating =
      item.averageRating != null && Number.isFinite(Number(item.averageRating))
        ? Number(item.averageRating)
        : null
    const ratingCounts = normalizeRatingCounts(item.ratingCounts)
    return {
      ...item,
      totalRatings,
      averageRating,
      ratingPercent:
        item.ratingPercent != null && Number.isFinite(Number(item.ratingPercent))
          ? Number(item.ratingPercent)
          : null,
      ...(ratingCounts ? { ratingCounts } : {}),
    }
  })

  const chart = data.chart
    ? {
        ...data.chart,
        available: Boolean(data.chart.available),
        items: Array.isArray(data.chart.items)
          ? data.chart.items.map((row) => ({
              rating: Number((row as { rating?: number }).rating) || 0,
              count: Number((row as { count?: number }).count) || 0,
              percent: Number((row as { percent?: number }).percent) || 0,
            }))
          : [],
        totals: {
          ...data.chart.totals,
          ratingPercent:
            data.chart.totals?.ratingPercent != null &&
            Number.isFinite(Number(data.chart.totals.ratingPercent))
              ? Number(data.chart.totals.ratingPercent)
              : null,
          totalRatings:
            data.chart.totals?.totalRatings != null
              ? Number(data.chart.totals.totalRatings) || 0
              : undefined,
          averageRating:
            data.chart.totals?.averageRating != null &&
            Number.isFinite(Number(data.chart.totals.averageRating))
              ? Number(data.chart.totals.averageRating)
              : data.chart.totals?.averageRating === null
                ? null
                : undefined,
        },
      }
    : data.chart

  return { ...data, items, chart }
}

export type VisitorsReportSummary = {
  totalVisitors: number
  activeNow: number
  avgTimeOnSiteSeconds: number
  newVisitors: number
}

export type VisitorsReportGeo = {
  ip?: string | null
  city?: string | null
  countryCode?: string | null
  countryName?: string | null
  timezone?: string | null
}

export type VisitorsReportBrowser = {
  language?: string | null
  platform?: string | null
  userAgent?: string | null
}

export type VisitorsReportItem = {
  sessionId: string
  visitorId: string
  contactId?: number | null
  chatId?: number | string | null
  name: string
  initials?: string | null
  isOnline: boolean
  userType?: string | null
  timeOnSiteSeconds: number
  channelType: string
  channelName: string
  siteId?: string | null
  pageUrl?: string | null
  referrer?: string | null
  deviceType?: string | null
  geo?: VisitorsReportGeo | null
  browser?: VisitorsReportBrowser | null
  startedAt?: string | null
  lastSeenAt?: string | null
}

export type VisitorsReportMeta = {
  page: number
  perPage: number
  total: number
  lastPage: number
  onlineWindowSec?: number
}

export type VisitorsReportData = {
  summary: VisitorsReportSummary
  items: VisitorsReportItem[]
  meta?: VisitorsReportMeta
}

export type VisitorsReportSortBy = 'timeOnSiteSeconds'
export type VisitorsReportSortOrder = 'ASC' | 'DESC'

export type VisitorsReportQueryParams = {
  widgetId?: number
  dateFrom: string
  dateTo: string
  channelType?: string
  channelId?: number
  /** Omitted → backend keeps its default order. Changing it always starts from page 1. */
  sortBy?: VisitorsReportSortBy
  sortOrder?: VisitorsReportSortOrder
  page?: number
  /**
   * Case-insensitive match on visitorId, siteId (domain), pageUrl and the visitor's name
   * (session client name, else customerName). Empty → no filter.
   */
  search?: string
}

export function buildVisitorsReportQuery(params: VisitorsReportQueryParams): string {
  const q = new URLSearchParams()
  if (params.widgetId != null && Number.isFinite(params.widgetId)) {
    q.set('widgetId', String(params.widgetId))
  }
  q.set('dateFrom', params.dateFrom)
  q.set('dateTo', params.dateTo)
  if (params.channelId != null && Number.isFinite(params.channelId)) {
    q.set('channelId', String(params.channelId))
  } else if (params.channelType) {
    q.set('channelType', params.channelType)
  }
  const search = String(params.search ?? '').trim()
  if (search) q.set('search', search)
  if (params.sortBy) {
    q.set('sortBy', params.sortBy)
    q.set('sortOrder', params.sortOrder === 'ASC' ? 'ASC' : 'DESC')
  }
  if (params.page != null && Number.isFinite(params.page) && params.page > 1) {
    q.set('page', String(Math.trunc(params.page)))
  }
  return q.toString()
}

type VisitorsReportResult =
  | { ok: true; data: VisitorsReportData }
  | { ok: false; status: number; notFound?: boolean }

const inflightVisitorsReports = new Map<string, Promise<VisitorsReportResult>>()

async function fetchVisitorsReportOnce(
  base: string,
  params: VisitorsReportQueryParams,
  authHeaders: Record<string, string>,
): Promise<VisitorsReportResult> {
  const url = `${base}/api/reports/visitors?${buildVisitorsReportQuery(params)}`
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
  })
  if (handleUnauthorized(res)) {
    return { ok: false, status: 401 }
  }
  if (res.status === 404) {
    return { ok: false, status: 404, notFound: true }
  }
  if (!res.ok) {
    return { ok: false, status: res.status }
  }
  const json = await res.json()
  if (!json?.success || !json?.data) {
    return { ok: false, status: res.status }
  }
  return { ok: true, data: normalizeVisitorsReportData(json.data) }
}

function readFiniteNumber(...values: unknown[]): number | null {
  for (const value of values) {
    if (value == null || value === '') continue
    const n = Number(value)
    if (Number.isFinite(n)) return n
  }
  return null
}

function readString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string') return value
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return ''
}

function isVisitorOnlineFlag(row: Record<string, unknown>): boolean {
  if (row.isOnline === true || row.is_online === true || row.online === true) return true
  const status = String(row.status ?? row.presence ?? '').trim().toLowerCase()
  return status === 'online' || status === 'active'
}

export function normalizeVisitorsReportItem(raw: unknown): VisitorsReportItem | null {
  const row = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null
  if (!row) return null
  const geoRaw = row.geo && typeof row.geo === 'object' && !Array.isArray(row.geo) ? (row.geo as Record<string, unknown>) : null
  const browserRaw =
    row.browser && typeof row.browser === 'object' && !Array.isArray(row.browser)
      ? (row.browser as Record<string, unknown>)
      : null
  const sessionId = readString(row.sessionId, row.session_id)
  const visitorId = readString(row.visitorId, row.visitor_id, sessionId)
  if (!sessionId && !visitorId) return null
  const contactId = readFiniteNumber(row.contactId, row.contact_id)
  return {
    sessionId: sessionId || visitorId,
    visitorId: visitorId || sessionId,
    contactId: contactId != null ? contactId : null,
    chatId: (row.chatId ?? row.chat_id ?? null) as VisitorsReportItem['chatId'],
    name: readString(row.name) || t('chat.client.anonymous'),
    initials: typeof row.initials === 'string' ? row.initials : null,
    isOnline: isVisitorOnlineFlag(row),
    userType: typeof row.userType === 'string' ? row.userType : typeof row.user_type === 'string' ? row.user_type : null,
    timeOnSiteSeconds: Math.max(0, readFiniteNumber(row.timeOnSiteSeconds, row.time_on_site_seconds) ?? 0),
    channelType: readString(row.channelType, row.channel_type) || 'WEB_CHAT',
    channelName: readString(row.channelName, row.channel_name),
    siteId: typeof row.siteId === 'string' ? row.siteId : typeof row.site_id === 'string' ? row.site_id : null,
    pageUrl: typeof row.pageUrl === 'string' ? row.pageUrl : typeof row.page_url === 'string' ? row.page_url : null,
    referrer: typeof row.referrer === 'string' ? row.referrer : null,
    deviceType:
      typeof row.deviceType === 'string' ? row.deviceType : typeof row.device_type === 'string' ? row.device_type : null,
    geo: geoRaw
      ? {
          ip: typeof geoRaw.ip === 'string' ? geoRaw.ip : null,
          city: typeof geoRaw.city === 'string' ? geoRaw.city : null,
          countryCode:
            typeof geoRaw.countryCode === 'string'
              ? geoRaw.countryCode
              : typeof geoRaw.country_code === 'string'
                ? geoRaw.country_code
                : null,
          countryName:
            typeof geoRaw.countryName === 'string'
              ? geoRaw.countryName
              : typeof geoRaw.country_name === 'string'
                ? geoRaw.country_name
                : null,
          timezone: typeof geoRaw.timezone === 'string' ? geoRaw.timezone : null,
        }
      : null,
    browser: browserRaw
      ? {
          language: typeof browserRaw.language === 'string' ? browserRaw.language : null,
          platform: typeof browserRaw.platform === 'string' ? browserRaw.platform : null,
          userAgent:
            typeof browserRaw.userAgent === 'string'
              ? browserRaw.userAgent
              : typeof browserRaw.user_agent === 'string'
                ? browserRaw.user_agent
                : null,
        }
      : null,
    startedAt: typeof row.startedAt === 'string' ? row.startedAt : typeof row.started_at === 'string' ? row.started_at : null,
    lastSeenAt:
      typeof row.lastSeenAt === 'string' ? row.lastSeenAt : typeof row.last_seen_at === 'string' ? row.last_seen_at : null,
  }
}

export function normalizeVisitorsReportData(raw: unknown): VisitorsReportData {
  const data = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}
  const summaryRaw =
    data.summary && typeof data.summary === 'object' && !Array.isArray(data.summary)
      ? (data.summary as Record<string, unknown>)
      : {}
  const items = (Array.isArray(data.items) ? data.items : [])
    .map(normalizeVisitorsReportItem)
    .filter((row): row is VisitorsReportItem => row != null)
  const fromSummary = readFiniteNumber(summaryRaw.activeNow, summaryRaw.active_now)
  const fromItems = items.reduce((n, row) => n + (row.isOnline ? 1 : 0), 0)
  const metaRaw =
    data.meta && typeof data.meta === 'object' && !Array.isArray(data.meta) ? (data.meta as Record<string, unknown>) : null
  return {
    summary: {
      totalVisitors: Math.max(0, Math.trunc(readFiniteNumber(summaryRaw.totalVisitors, summaryRaw.total_visitors) ?? 0)),
      activeNow: Math.max(0, Math.trunc(fromSummary ?? 0), fromItems),
      avgTimeOnSiteSeconds: Math.max(
        0,
        Math.trunc(readFiniteNumber(summaryRaw.avgTimeOnSiteSeconds, summaryRaw.avg_time_on_site_seconds) ?? 0),
      ),
      newVisitors: Math.max(0, Math.trunc(readFiniteNumber(summaryRaw.newVisitors, summaryRaw.new_visitors) ?? 0)),
    },
    items,
    meta: metaRaw
      ? {
          page: Math.max(1, Math.trunc(readFiniteNumber(metaRaw.page) ?? 1)),
          perPage: Math.max(0, Math.trunc(readFiniteNumber(metaRaw.perPage, metaRaw.per_page) ?? 0)),
          total: Math.max(0, Math.trunc(readFiniteNumber(metaRaw.total) ?? 0)),
          lastPage: Math.max(1, Math.trunc(readFiniteNumber(metaRaw.lastPage, metaRaw.last_page) ?? 1)),
          onlineWindowSec:
            readFiniteNumber(metaRaw.onlineWindowSec, metaRaw.online_window_sec) != null
              ? Math.max(0, Math.trunc(readFiniteNumber(metaRaw.onlineWindowSec, metaRaw.online_window_sec) ?? 0))
              : undefined,
        }
      : undefined,
  }
}

export function fetchVisitorsReport(
  base: string,
  params: VisitorsReportQueryParams,
  authHeaders: Record<string, string>,
): Promise<VisitorsReportResult> {
  const normalizedBase = base.replace(/\/$/, '')
  const dedupeKey = `${normalizedBase}|${buildVisitorsReportQuery(params)}|${authHeaders.Authorization ?? ''}`
  const existing = inflightVisitorsReports.get(dedupeKey)
  if (existing) return existing

  const promise = fetchVisitorsReportOnce(normalizedBase, params, authHeaders).finally(() => {
    if (inflightVisitorsReports.get(dedupeKey) === promise) {
      inflightVisitorsReports.delete(dedupeKey)
    }
  })
  inflightVisitorsReports.set(dedupeKey, promise)
  return promise
}

export type OperatorWorktimeInterval = {
  startedAt: string
  endedAt: string
  durationSeconds: number
  startSecondOfDay: number
  endSecondOfDay: number
}

export type OperatorWorktimeItem = {
  operatorId: number
  displayName: string
  title: string | null
  avatarUrl: string | null
  totalOnlineSeconds: number
  intervals: OperatorWorktimeInterval[]
}

export type OperatorWorktimeReportData = {
  date: string
  dayStartedAt: string
  dayEndedAt: string
  utcOffsetMinutes: number
  timezone: string
  operators: OperatorWorktimeItem[]
}

export type OperatorWorktimeQueryParams = {
  widgetId?: number
  date: string
  utcOffsetMinutes?: number
}

export function buildOperatorWorktimeQuery(params: OperatorWorktimeQueryParams): string {
  const q = new URLSearchParams()
  if (params.widgetId != null && Number.isFinite(params.widgetId)) {
    q.set('widgetId', String(params.widgetId))
  }
  q.set('date', params.date)
  const utcOffset = params.utcOffsetMinutes ?? -new Date().getTimezoneOffset()
  q.set('utcOffsetMinutes', String(utcOffset))
  return q.toString()
}

type OperatorWorktimeReportResult =
  | { ok: true; data: OperatorWorktimeReportData }
  | { ok: false; status: number; notFound?: boolean }

const inflightOperatorWorktimeReports = new Map<string, Promise<OperatorWorktimeReportResult>>()

async function fetchOperatorWorktimeReportOnce(
  base: string,
  params: OperatorWorktimeQueryParams,
  authHeaders: Record<string, string>,
): Promise<OperatorWorktimeReportResult> {
  const url = `${base}/api/reports/operator-worktime?${buildOperatorWorktimeQuery(params)}`
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
  })
  if (handleUnauthorized(res)) {
    return { ok: false, status: 401 }
  }
  if (res.status === 404) {
    return { ok: false, status: 404, notFound: true }
  }
  if (!res.ok) {
    return { ok: false, status: res.status }
  }
  const json = await res.json()
  if (!json?.success || !json?.data) {
    return { ok: false, status: res.status }
  }
  return { ok: true, data: json.data as OperatorWorktimeReportData }
}

export function fetchOperatorWorktimeReport(
  base: string,
  params: OperatorWorktimeQueryParams,
  authHeaders: Record<string, string>,
): Promise<OperatorWorktimeReportResult> {
  const normalizedBase = base.replace(/\/$/, '')
  const dedupeKey = `${normalizedBase}|${buildOperatorWorktimeQuery(params)}|${authHeaders.Authorization ?? ''}`
  const existing = inflightOperatorWorktimeReports.get(dedupeKey)
  if (existing) return existing

  const promise = fetchOperatorWorktimeReportOnce(normalizedBase, params, authHeaders).finally(() => {
    if (inflightOperatorWorktimeReports.get(dedupeKey) === promise) {
      inflightOperatorWorktimeReports.delete(dedupeKey)
    }
  })
  inflightOperatorWorktimeReports.set(dedupeKey, promise)
  return promise
}

export type WidgetListItem = { id: number; name: string }

export type UserWidgetsAndChannels = {
  widgets: WidgetListItem[]
  channels: ReportChannelOption[]
}

export function parseWidgetsAndChannelsFromApi(json: {
  success?: boolean
  data?: { items?: unknown[] }
}): UserWidgetsAndChannels {
  if (!json?.success || !Array.isArray(json?.data?.items)) {
    return { widgets: [], channels: [] }
  }

  const widgets: WidgetListItem[] = []
  const channels: ReportChannelOption[] = []
  const seenChannelIds = new Set<number>()
  const multipleWidgets = json.data.items.length > 1

  for (const raw of json.data.items) {
    const w = raw as {
      widget_id?: number
      id?: number
      name?: string
      channels?: Array<{ id?: number; name?: string; type?: string }>
    }
    const widgetId = Number(w.widget_id ?? w.id)
    if (!Number.isFinite(widgetId)) continue

    const widgetName = typeof w.name === 'string' && w.name.trim() ? w.name.trim() : `${t('nav.widget')} ${widgetId}`
    widgets.push({ id: widgetId, name: widgetName })

    if (!Array.isArray(w.channels)) continue
    for (const ch of w.channels) {
      const id = Number(ch.id)
      if (!Number.isFinite(id) || seenChannelIds.has(id)) continue
      seenChannelIds.add(id)
      const channelType = typeof ch.type === 'string' ? ch.type : String(ch.type ?? '')
      const channelName =
        typeof ch.name === 'string' && ch.name.trim() ? ch.name.trim() : channelType
      channels.push({
        id,
        widgetId,
        channelType,
        name: multipleWidgets ? `${channelName} (${widgetName})` : channelName,
      })
    }
  }

  return { widgets, channels }
}

/** @deprecated Используйте store.userWidgets.ensureLoaded */
export async function fetchUserWidgetsAndChannels(
  base: string,
  authHeaders: Record<string, string>,
): Promise<UserWidgetsAndChannels> {
  const res = await fetch(buildWidgetListUrl(base, { page: 1, limit: 100 }), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
  })
  if (handleUnauthorized(res) || !res.ok) return { widgets: [], channels: [] }
  const json = await res.json()
  return parseWidgetsAndChannelsFromApi(json)
}

/** @deprecated Используйте fetchUserWidgetsAndChannels */
export async function fetchWidgetsForReports(
  base: string,
  authHeaders: Record<string, string>,
): Promise<WidgetListItem[]> {
  const { widgets } = await fetchUserWidgetsAndChannels(base, authHeaders)
  return widgets
}

/** Значение фильтра в MatSelect → параметры отчёта. */
export function parseReportChannelFilter(filter: string): {
  channelType?: string
  channelId?: number
} {
  if (!filter || filter === 'all') return {}
  if (filter.startsWith('id:')) {
    const channelId = Number(filter.slice(3))
    return Number.isFinite(channelId) ? { channelId } : {}
  }
  return { channelType: filter }
}
